/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactElement, useState} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {Account} from '@ion/core/src/types/entities';
import {TertiaryButton} from '../components/buttons/TertiaryButton';
import GroupChooser from '../editors/GroupChooser';
import {dlsTypography, dlsZIndex} from '../theme/theme';
import {Color, DLSTheme} from '../types/types';

// defines the prop definitions for the session timeout ...
export type RemoveDeviceNotificationProps = {
  icon?: ReactElement;
  capitalize?: boolean;
  show: boolean;
  title: string;
  actionLabel?: string | JSX.Element;
  dismissLabel?: string;
  zIndex?: number;
  onConfirm?: (group: Account) => void;
  onCancel?: () => void;
  account?: Account;
  movedToAccount?: object;
  hasDeviceMoveOption?: boolean;
};

// defines the styles used by this component ...
const useStyles = makeStyles<DLSTheme, RemoveDeviceNotificationProps>(({dls}) => {
  const overlayIndex = dlsZIndex('userNotificationDialogRoot');
  return {
    icon : {
      display       : 'inline-block',
      verticalAlign : 'top',
      paddingTop    : 2,
      marginRight   : 6
    },
    paper : {
      borderRadius : 0
    },
    userNotificationRoot : {
      zIndex : ({zIndex}) => `${zIndex ?? overlayIndex} !important` as any
    },
    title : {
      ...dlsTypography('h4'),
      textTransform : ({capitalize}) => capitalize ? 'uppercase' : 'none',
      padding       : '20px 20px 0px 24px',
      lineHeight    : '24px',
      letterSpacing : 1
    },
    content : {
      padding : '0 24px !important',
      margin  : '10px 0 15px'
    },
    actions : {
      justifyContent : 'flex-start',
      padding        : '0px 18px 8px !important',
      marginTop      : -6
    },
    button : {
      padding : 0
    }
  };
});

/**
 * The RemoveDeviceNotification prompt serves to provide a notification prompt
 * which is to be used for prompting a user after disassociating a device from an Asset and
 * move it to a different group.
 *
 * @see https://www.figma.com/file/z9yUzIaskZRhgpYIhLyh91/03_Hero-Screens?node-id=632%3A82003
 *
 *
 * ```typescript
 *
 *   import {RemoveDeviceNotification} from '@ion/dls/src/notifications';
 *
 *   export const SessionTimeout = ({show, onLogin}: SessionTimeoutProps) => {
 *     return (
 *       <RemoveDeviceNotification show={show}
 *         icon={<NotificationDisconnectIcon />}
 *         title={translate('notifications:session.title')}
 *         message={translate('notifications:session.message')}
 *         actionLabel={translate('notifications:session.login')}
 *         onAction={onLogin} />
 *     );
 *   };
 *
 * ```
 *
 */
export const RemoveDeviceNotification = (props: RemoveDeviceNotificationProps) => {
  const {
    icon,
    show,
    title,
    actionLabel,
    dismissLabel,
    onConfirm,
    onCancel,
    account,
    movedToAccount,
    hasDeviceMoveOption
  } = props;
  const classes = useStyles(props);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupValue, setGroupValue]       = useState(null);
  if (show) {
    const hasActions = true; //Boolean(onAction || onDismiss);
    return (
      <Dialog open classes={{
        root : classes.userNotificationRoot
      }}>
        <DialogTitle className={classes.title}>
          {icon && (
            <div className={classes.icon}>{icon}</div>
          )}
          {title}
        </DialogTitle>
        <DialogContent className={classes.content}>
          <div>
            <GroupChooser account={account}
              onLoad={setGroupsLoading}
              notificationGroupChooser
              notificationFieldLabel={translate('notifications:dialog.moveGroup')}
              onChange={function(value) {
                setGroupValue(value);
              }}
              movedToAccount={movedToAccount}
              hasDeviceMoveOption={hasDeviceMoveOption}
              defaultValue={account} />
          </div>
        </DialogContent>
        {hasActions && (
          <DialogActions className={classes.actions}>
            <TertiaryButton className={classes.button}
              disabled={groupsLoading}
              onClick={function() {
                onConfirm(groupValue);
              }}>
              {actionLabel}
            </TertiaryButton>
            <TertiaryButton onClick={onCancel} isAlt altColor={Color.grey3}>
              {dismissLabel}
            </TertiaryButton>
          </DialogActions>
        )}
      </Dialog>
    );
  }
  return null;
};

export default RemoveDeviceNotification;
